import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { fadeIn, fadeOut } from "../../../animations";
import { GlobalSettingsService } from "src/app/services/ui/global-settings.service";
import { FormBuilder, Validators } from "@angular/forms";
import { GenericModalComponent } from "../../generic-modal/generic-modal.component";
import { BinderWizardService } from "src/app/services/sections/binder/binder-wizard.service";
import { Observable, Subject, of } from "rxjs";
import { LocationService } from "src/app/services/location.service";
import { ToastService } from "src/app/services/ui/toast.service";
import { ToastType } from "../../toast/models/toast-type.enum";
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil, tap } from "rxjs/operators";
import { Router } from "@angular/router";
import { AddressModel } from "src/app/models/address/address.model";
import { BinderAddressService } from "src/app/services/address/binder-address.service";

@Component({
	selector: "virtual-binder-access",
	templateUrl: "./binder-access.component.html",
	styleUrls: [
		"./binder-access.component.scss",
		"../../stylesheets/job-actions.scss",
		"../../stylesheets/job-main.scss",
		"../../stylesheets/job-inputs.scss",
		"../../stylesheets/forms-container.scss",
	],
	animations: [fadeIn(200), fadeOut(200)]
})
export class BinderAccessComponent implements OnInit, OnDestroy {
	@ViewChild('lookupModal') public lookupModal: GenericModalComponent;
	public siteAddresses$: Observable<Array<any>>;
	public binders = [];
	public isLoading = false;
	public selectedAddress: AddressModel;
	public isAddressSelected = false;
	public autocomplete: any;

	public addressPredictions$: Observable<Array<any>>;

	public readonly defaultGeoCode: { latitude: number, longitude: number } = { latitude: 37.09024, longitude: -95.712891 }; // US
	public readonly defaultCountry: { id: number, name: string } = { id: 1, name: 'United States of America' };
	public countries: { id: number, name: string }[] = [];
	public states: { id: number, name: string, code: string }[] = [];

	public lookupAddressFormGroup = this.formBuilder.group({
		search: [''],
		address: []
	});

	public binderFormGroup = this.formBuilder.group({
		address: ['', [Validators.required]],
		binder: [{ value: null, disabled: true }, [Validators.required]],
		email: ['', [Validators.required, Validators.email, Validators.minLength(3), Validators.pattern(/\S+/)]],
		hasWorkToDo: [false]
	});

	public currentLocation: { latitude?: any, longitude?: any, name?: string } = { name: '' };
	private unsubscriber$: Subject<void> = new Subject<void>();

	constructor(
		private formBuilder: FormBuilder,
		private binderService: BinderWizardService,
		private locationService: LocationService,
		private router: Router,
		private binderAddressService: BinderAddressService
	) { }

	public onLookup() {
		this.lookupModal.open();
		this.getLocation();
	}

	public onChooseAddress() {
		const address = this.lookupAddressFormGroup.get('address').value[0];
		if (address) {
			this.binderFormGroup.patchValue({ address: address.addressId });
			this.onCloseLookup();
			this.selectedAddress = address;
			this.binders = [];
			this.binderFormGroup.patchValue({ binder: null });
			this.getBindersByAddress(address.addressId);
		}
	}

	public isAnyAddressSelected(): boolean {
		const address = this.lookupAddressFormGroup.get('address').value;
		return address && address[0] ? true : false;
	}

	public onCloseLookup() {
		this.lookupModal.hide();
		this.lookupAddressFormGroup.patchValue({ search: '', address: [] });
		this.addressPredictions$ = of([]);
	}

	ngOnInit(): void {
		GlobalSettingsService.isGlobalLoaderLoading = false;
		GlobalSettingsService.isSecondaryLoaderLoading = false;
		this.getLocation(true);
		const cachedEmail = localStorage.getItem('cloud-email');
		if (cachedEmail) {
			this.binderFormGroup.patchValue({ email: cachedEmail });
		}
		
		this.initializeAddressFormChanges();
	}

	public getAddressLabel(adressObj) {
		let addressLabel = '';
		if (adressObj.city) {
			addressLabel = `${adressObj.line1}, ${adressObj.details}`;
		}
		if (adressObj.length > 0) {
			addressLabel = adressObj;
		}
		return addressLabel;
	}

	public onSubmit() {
		const formData = this.binderFormGroup.value;
		this.router.navigate([formData.binder.redirectUri],
			{
				state: {
					selectedBinder: formData.binder,
					email: formData.email,
					hasWorkToDo: formData.hasWorkToDo,
					site: this.selectedAddress.line1,
					latitude: this.selectedAddress.latitude,
					longitude: this.selectedAddress.longitude
				}
			});
	}

	private getLocation(isCheckNearbySiteAddress = false) {
		this.isLoading = true;
		this.locationService
			.getLocation()
			.pipe(
				takeUntil(this.unsubscriber$)
			)
			.subscribe(
				result => {
					if (result && result.coords) {
						this.currentLocation = { latitude: result.coords.latitude, longitude: result.coords.longitude };
						this.getNearbyAddresses(this.currentLocation.latitude, this.currentLocation.longitude);
						this.getSiteAddresses(this.currentLocation.latitude, this.currentLocation.longitude, isCheckNearbySiteAddress);
					}
					else{
						this.currentLocation.name = 'undetermined';
						this.isLoading = false;
					}

					if (!isCheckNearbySiteAddress) {
						this.isLoading = false;
					}
				},
				err => {
					this.currentLocation = { name: 'undetermined' };
					switch (err.code) {
						case LocationService.PERMISSION_DENIED:
							ToastService.pushToast(
								{
									text: 'Please enable location service!',
									icon: 'error',
									type: ToastType.Error
								}
							);
							break;
						case LocationService.UNSUPPORTED_BROWSER:
							ToastService.pushToast(
								{
									text: 'Geolocation is not supported by your browser!',
									icon: 'error',
									type: ToastType.Error
								}
							);
							break;
						default:
							ToastService.pushToast(
								{
									text: err.message,
									icon: 'error',
									type: ToastType.Error
								}
							);
							break;
					}

					this.isLoading = false;
				}
			);
	}

	ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public cloneObject(obj: any): any {
		return JSON.parse(JSON.stringify(obj));
	}

	public getGeoLocationAddresses(address: string) {
		this.addressPredictions$ = this.binderAddressService
			.getAddressPredictions(address)
			.pipe(
				catchError(() => {
					return of([]);
				}),
				takeUntil(this.unsubscriber$)
			);	
	}

	public getNearbyAddresses(lat: number, lng: number) {
		this.binderAddressService.getAddressesByCoordinates(lat, lng)
			.pipe(
				takeUntil(this.unsubscriber$)
			)
			.subscribe(
				(result) => {
					if (result && result.length > 0) {
						const a = result[0];
						let address = a.line1;
						if (a.line2) {
							address += `, ${a.line1}`;
						}
						address += `, ${a.city}`;
						if (a.code2) {
							address += `, ${a.code2}`;
						}
						if (a.zipCode) {
							address += `, ${a.zipCode}`;
						}
						this.currentLocation.name = address;
						this.lookupAddressFormGroup.patchValue({ search: this.currentLocation.name });
					}
					else {
						this.currentLocation = { name: 'undetermined' };
					}
				},
				() => {
					this.currentLocation = { name: 'undetermined' };
				}
			);
	}

	public getSiteAddresses(lat, long, isCheckNearbySiteAddress = false) {
		this.siteAddresses$ = this.binderService
			.getSiteAddresses(lat, long)
			.pipe(
				tap(result => {
					if (isCheckNearbySiteAddress) {
						if (result.length > 0) {
							this.binderFormGroup.patchValue({ address: result[0].addressId });
							this.selectedAddress = result[0];
							this.getBindersByAddress(this.selectedAddress.addressId);
						}
						else {
							this.isLoading = false;
						}
					} else {
						this.isLoading = false;
					}
				}),
				catchError(() => {
					this.isLoading = false;
					return of([]);
				}),
				takeUntil(this.unsubscriber$)
			);

		if (isCheckNearbySiteAddress) {
			this.siteAddresses$.subscribe();
		}
	}

	public onClearAddress() {
		this.lookupAddressFormGroup.patchValue({ search: ''});
		this.siteAddresses$ = of([]);
		this.addressPredictions$ = of([]);
	}

	private initializeAddressFormChanges() {
		this.lookupAddressFormGroup
			.get('search')
			.valueChanges
			.pipe(
				debounceTime(800),
				distinctUntilChanged(),
				map(x => {
					let addressDescription = '';
					if (x.city) {
						addressDescription = `${x.line1}, ${x.details}`
						this.getSiteAddresses(x.latitude, x.longitude);
					}
					else {
						addressDescription = x;
					}
					return addressDescription;
				}),
				takeUntil(this.unsubscriber$)
			)
			.subscribe(res => {
				if (res) {
					if (res.length >= 3) {
						this.getGeoLocationAddresses(res);
					}
				}
				else {
					this.siteAddresses$ = of([]);
					this.lookupAddressFormGroup.patchValue({ search: '', address: [] });
				}
			});
	}

	private getBindersByAddress(addressId) {
		this.isLoading = true;
		this.binderService
			.getBindersByAddress(addressId)
			.pipe(
				takeUntil(this.unsubscriber$)
			)
			.subscribe(
				result => {
					this.binders = result;

					if (this.binders.length > 0) {
						this.binderFormGroup.get('binder').enable();
					}
					else {
						this.binderFormGroup.get('binder').disable();
					}

					this.isLoading = false;
				},
				() => {
					this.binders = [];
					this.isLoading = false;
				}
			);
	}
}
